import React, { useEffect, useState } from "react";
import { Smartphone, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";

// Note: STORAGE_KEY holds only a millisecond timestamp of when the user
// dismissed the install banner — no PII, no tokens, no user data. Safe for
// localStorage; using cookies here would just add an HTTP round-trip.
const STORAGE_KEY = "hh-install-dismissed";

/**
 * PWA install prompt.
 * - Android / Desktop Chrome: uses the native `beforeinstallprompt` event.
 * - iOS Safari: shows a small inline guide because iOS doesn't fire the event.
 * Hidden once installed (display-mode: standalone) or user dismisses for 14 days.
 */
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [showIOS, setShowIOS] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    // Already installed?
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Dismissed recently?
    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;

    setHidden(false);

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari detection (no beforeinstallprompt support)
    const ua = window.navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    if (isIOS) setShowIOS(true);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") {
      setHidden(true);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setHidden(true);
  };

  if (hidden) return null;
  if (!deferred && !showIOS) return null;

  return (
    <div
      data-testid="install-pwa-banner"
      className="warm-card p-5 mb-6 flex items-start gap-4 border-hearth-clay/30 bg-hearth-clay/5"
    >
      <div className="w-10 h-10 rounded-2xl bg-hearth-clay/15 text-hearth-clayDark grid place-items-center shrink-0">
        <Smartphone size={18} strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-display text-xl text-hearth-ink leading-tight">
          Add HearthHouse to your home screen
        </h4>
        <p className="text-sm text-hearth-mute mt-1">
          {showIOS
            ? "Tap the share button below, then ‘Add to Home Screen’ — runs full-screen like a real app."
            : "One tap and HearthHouse lives in your dock like a native app — opens faster, full-screen, no browser bar."}
        </p>
        {showIOS && (
          <p className="caption mt-2 inline-flex items-center gap-1.5 text-hearth-clayDark">
            <Share size={12} /> Share → Add to Home Screen
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        {deferred && (
          <Button
            data-testid="install-pwa-button"
            onClick={install}
            className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-4 h-9 text-sm"
          >
            Install
          </Button>
        )}
        <button
          data-testid="install-pwa-dismiss"
          onClick={dismiss}
          className="text-xs text-hearth-mute hover:text-hearth-ink inline-flex items-center gap-1 justify-center"
        >
          <X size={11} /> Not now
        </button>
      </div>
    </div>
  );
}
